import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { SharedModule } from '@shared/shared.module';
import { VaultDateTimePipe } from '@shared/pipes/vault-date-time.pipe';
import { StandardAccountDictionaryProposal } from '@interfaces/accounting-data-standardization/responses/standard-account-dictionary-governance.response';
import {
  currentItemsForProposal,
  proposalKindLabel,
  proposalKindSection,
  proposalStatusLabel,
  proposedItemText,
  statementTypeLabel,
} from '@src/app/features/dashboard/accounting-data-standardization/utils/standard-account-dictionary-governance.util';

/** Itens do verbete atual mostrados no cartão; o resto vira "e mais N". */
const MAX_CURRENT_ITEMS = 6;

/** Pedido para abrir o documento que motivou a proposta. */
export interface DictionaryProposalDocumentRequest {
  companyId: string;
  extractionId: string;
}

/**
 * Cartão de uma proposta do revisor do De/Para para o dicionário (PBI 3423, DPA-DIC-DB). À esquerda o que o revisor
 * propõe (texto novo destacado, motivo, linha e documento que motivaram); à direita a seção do verbete atual que muda,
 * com o item novo já no lugar (em verde) ou, na definição, a atual riscada. Só apresentação: as ações saem por output.
 */
@Component({
  selector: 'app-dictionary-proposal-card',
  standalone: true,
  imports: [SharedModule, VaultDateTimePipe],
  templateUrl: './dictionary-proposal-card.component.html',
  styleUrl: './dictionary-proposal-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DictionaryProposalCardComponent {
  readonly proposal = input.required<StandardAccountDictionaryProposal>();
  /** Pode aplicar/recusar (permissão MANAGE). */
  readonly canManage = input(false);
  /** Esta proposta está sendo aplicada agora. */
  readonly busy = input(false);
  /** Outra ação do painel está em andamento: trava os botões. */
  readonly locked = input(false);

  readonly applyRequested = output<StandardAccountDictionaryProposal>();
  readonly editApplyRequested = output<StandardAccountDictionaryProposal>();
  readonly rejectRequested = output<StandardAccountDictionaryProposal>();
  readonly openDocumentRequested = output<DictionaryProposalDocumentRequest>();

  protected readonly kindLabel = computed(() => proposalKindLabel(this.proposal().kind));
  protected readonly sectionLabel = computed(() => proposalKindSection(this.proposal().kind));
  protected readonly statusLabel = computed(() => proposalStatusLabel(this.proposal().status));
  protected readonly statementLabel = computed(() =>
    statementTypeLabel(this.proposal().statementType)
  );
  protected readonly isDefinition = computed(() => this.proposal().kind === 'Definition');
  protected readonly isPending = computed(() => this.proposal().status === 'Pending');
  protected readonly newText = computed(() => proposedItemText(this.proposal()));

  private readonly currentItems = computed(() => currentItemsForProposal(this.proposal()));
  /** Os últimos itens da seção (o novo entra no fim), para o cartão não crescer demais. */
  protected readonly shownItems = computed(() => this.currentItems().slice(-MAX_CURRENT_ITEMS));
  protected readonly hiddenItemCount = computed(() =>
    Math.max(0, this.currentItems().length - MAX_CURRENT_ITEMS)
  );
  /** Definição de hoje (só em `Definition`). */
  protected readonly currentDefinition = computed(() =>
    this.isDefinition() ? (this.currentItems()[0] ?? null) : null
  );

  protected readonly canOpenDocument = computed(() => {
    const proposal = this.proposal();
    return !!proposal.companyId && !!proposal.revenueStatementExtractionId;
  });

  protected openDocument(): void {
    const proposal = this.proposal();
    if (!proposal.companyId || !proposal.revenueStatementExtractionId) return;
    this.openDocumentRequested.emit({
      companyId: proposal.companyId,
      extractionId: proposal.revenueStatementExtractionId,
    });
  }
}
